import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { oidcConfig, type OIDCSettings, type OIDCSettingsBody } from "@/api/adminPanel";
import { Badge, Button, Card, CardBody, CardHeader, FormError, Input, Label } from "@/components/ui";
import { useToast } from "@/components/Toast";

export function AdminOidcPage() {
  const toast = useToast();
  const cfg = useQuery({ queryKey: ["oidc"], queryFn: () => oidcConfig.get() });

  const [form, setForm] = useState<OIDCSettingsBody>({});
  const [scopesText, setScopesText] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!cfg.data) return;
    setForm({
      issuer: cfg.data.issuer ?? "",
      client_id: cfg.data.client_id ?? "",
      redirect_uri: cfg.data.redirect_uri ?? "",
    });
    setScopesText((cfg.data.scopes ?? []).join(" "));
    setSecret("");
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: (body: OIDCSettingsBody) => oidcConfig.save(body),
    onSuccess: () => {
      toast.push("OIDC settings saved", "success");
      cfg.refetch();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  function update<K extends keyof OIDCSettingsBody>(k: K, v: string) {
    setForm((cur) => ({ ...cur, [k]: v }));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const body: OIDCSettingsBody = {
      issuer: form.issuer || null,
      client_id: form.client_id || null,
      redirect_uri: form.redirect_uri || null,
      scopes: scopesText.trim() ? scopesText.split(/\s+/) : null,
    };
    if (secret) body.client_secret = secret;
    save.mutate(body);
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">OIDC / SSO</h1>

      <Card>
        <CardHeader>
          <div>
            <h2 className="text-sm font-semibold">Configure your IdP</h2>
            <p className="mt-1 text-xs text-slate-500">
              These values feed{" "}
              <code>/auth/oidc/<strong>{cfg.data?.issuer ? "<your-slug>" : "<your-slug>"}</strong>/login</code>.
              The client secret is encrypted at rest and never returned in reads.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          {cfg.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3">
              <div>
                <Label>Issuer</Label>
                <Input
                  placeholder="https://acme.okta.com"
                  value={form.issuer ?? ""}
                  onChange={(e) => update("issuer", e.target.value)}
                />
              </div>
              <div>
                <Label>Client ID</Label>
                <Input
                  placeholder="0oabc123…"
                  value={form.client_id ?? ""}
                  onChange={(e) => update("client_id", e.target.value)}
                />
              </div>
              <div>
                <Label>
                  Client secret {cfg.data?.client_secret_set && <Badge tone="good">stored</Badge>}
                </Label>
                <Input
                  type="password"
                  placeholder={cfg.data?.client_secret_set ? "(leave empty to keep existing)" : "client secret"}
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </div>
              <div>
                <Label>Scopes (space-separated)</Label>
                <Input
                  placeholder="openid email profile"
                  value={scopesText}
                  onChange={(e) => setScopesText(e.target.value)}
                />
              </div>
              <div>
                <Label>Redirect URI</Label>
                <Input
                  placeholder="https://trade.acme.com/auth/oidc/acme/callback"
                  value={form.redirect_uri ?? ""}
                  onChange={(e) => update("redirect_uri", e.target.value)}
                />
              </div>
              <FormError>{error}</FormError>
              <div className="flex justify-end pt-1">
                <Button type="submit" loading={save.isPending}>
                  Save
                </Button>
              </div>
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
